import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { WithFormStyles, withFormStyles } from '../../../util/withFormStyles';
import { Form } from '@calefy-inc/informedMaterial';
import { useSelector } from 'react-redux';
import { StoreState } from '../../../store';
import {
  answerInstancesToFormState,
  formStateToAnswerInstances,
} from '../../QuoteWizard/QuoteForms/utility';
import RenderedQuestionInstance from '../../common/QuoteComponents/RenderedQuestionInstance';
import { QuoteWizardForm } from '../../QuoteWizard/classes';
import { getPolicyStreamFormLabelFromInternalName } from './utility';
import { generateGetFormByInternalName } from '../../../store/policystreamStore';
import { generateSubmitPolicyStreamFormAnswersThunk } from '../../../store/policystreamStore';
import { POLICYSTREAM_FORMS } from '../constants';
import { policyStreamPaths } from '../policyStreamConstants';
import { Link } from '@reach/router';

const getInitialValuesMatchingInternalName = (
  internalName: string | null | undefined,
) => {
  return (store: StoreState) => {
    const matching = Object.values(store.policystreamStore.formAnswers).find(
      (answeredForm) => answeredForm.form.internalName === internalName,
    );
    const initialValues = matching
      ? answerInstancesToFormState(matching.answers)
      : undefined;
    return initialValues;
  };
};

interface PolicyStreamFormEditButtonProps {
  internalName: NonNullable<QuoteWizardForm['internalName']>;
}
export const PolicyStreamFormEditButton = ({
  internalName,
}: PolicyStreamFormEditButtonProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [label, setLabel] = useState<string>('');
  const [isNavigateButton, setIsNavigateButton] = useState<boolean>(false); // usually this button opens up a modal with the form; should it instead navigate them somewhere?
  const [navigationDestination, setNavigationDestination] =
    useState<string>('');

  // determine whether this is a navigation button
  useEffect(() => {
    if (internalName === POLICYSTREAM_FORMS.needsAnalysis) {
      setIsNavigateButton(true);
      setNavigationDestination(policyStreamPaths.personalize.url);
    } else {
      setIsNavigateButton(false);
      setNavigationDestination('');
    }
  }, [internalName]);

  useEffect(() => {
    setLabel(getPolicyStreamFormLabelFromInternalName(internalName));
  }, [internalName]);

  return (
    <>
      <Tooltip
        title={label}
        enterDelay={100}
        leaveDelay={100}
        placement='right'
      >
        {isNavigateButton ? (
          <Button component={Link} to={navigationDestination}>
            Go To Form
          </Button>
        ) : (
          <IconButton
            aria-label={label}
            onClick={() => {
              setOpen(true);
            }}
          >
            <EditIcon fontSize='small' />
          </IconButton>
        )}
      </Tooltip>
      <EditFormDialog
        open={open}
        onClose={() => setOpen(false)}
        internalName={internalName}
      />
    </>
  );
};

interface EditFormDialogProps extends WithFormStyles {
  open: boolean;
  onClose: () => void;
  internalName: string;
}
const EditFormDialog = withFormStyles()(
  ({ open, onClose, classes, internalName }: EditFormDialogProps) => {
    const getFormByInternalName = useCallback(
      generateGetFormByInternalName(internalName),
      [internalName],
    );
    const form = useSelector(getFormByInternalName);
    const initialValues = useSelector(
      getInitialValuesMatchingInternalName(internalName),
    );
    const dispatch = useDispatch();
    if (!form) {
      return null;
    }
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          {getPolicyStreamFormLabelFromInternalName(internalName)}
        </DialogTitle>
        <Form
          className={classes.dialogForm}
          initialValues={initialValues}
          onSubmit={(formState) => {
            const { values } = formState;
            dispatch(
              generateSubmitPolicyStreamFormAnswersThunk({
                form,
                answers: formStateToAnswerInstances(
                  values,
                  form.questionInstances,
                ),
              }),
            );
            onClose();
          }}
        >
          <DialogContent sx={{ minWidth: '600px' }}>
            {form.questionInstances.map((questionInstance) => (
              <RenderedQuestionInstance
                questionInstance={questionInstance}
                classes={classes}
                key={questionInstance.id}
              />
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button type='submit'>Submit</Button>
          </DialogActions>
        </Form>
      </Dialog>
    );
  },
);
